"use strict";

import {onFind} from "@elements/init-modules-in-scope";
import {ISOStringToDate, UTCDateToLocalDate, dateToISOString, localDateToUTCDate} from "@elements/date-utils";
import {findIn, trigger, on, off, closest, hasClass} from "@elements/dom-utils";
import {getPrefixedDataSet} from "@elements/data-set-utils";
import flatpickr from "flatpickr";

const defaultOptions = {
    mode: "single",
    minDate: "today",
    showMonths: 1,
    disableMobile: "true",
    appendAltTime: true,
    defaultRange: 7,
    rangeSeparator: null
};

const defaultSelectors = {
    base: '.js-datepicker',
    input: '.js-datepicker__input',
    altField: '.js-datepicker__alt-field',
    altFieldTo: '.js-datepicker__alt-field-to'
};

export function init(options = defaultOptions, selectors = defaultSelectors, scope = document.body) {
    onFind(selectors.base, function (baseElement) {
        createDatepicker(
            baseElement,
            {...defaultOptions, appendTo: scope, ...options},
            {...defaultSelectors, ...selectors}
        );

        on('focusin', (e) => {
            if(closest('.flatpickr-calendar', e.target) !== null) {
                e.stopImmediatePropagation();
            }
        }, document);
    }, scope);
}

export function createDatepicker(baseElement, defaultOptions, selectors) {
    let datepicker = null;
    let input = findIn(selectors.input, baseElement);
    let altField = findIn(selectors.altField, baseElement);
    let altFieldTo = findIn(selectors.altFieldTo, baseElement);
    let dataSet = getPrefixedDataSet('datepicker', baseElement)

    const onDayCreateFunctions = [
        function(dObj, dStr, fp, dayElem) {
            if (!hasClass('flatpickr-disabled', dayElem) && !hasClass('hidden', dayElem)) {
                dayElem.setAttribute('role', 'button');
            }
        }
    ]
    const onDayCreateGiven = defaultOptions.onDayCreate;
    if  (onDayCreateGiven) {
        delete defaultOptions.onDayCreate;
        onDayCreateFunctions.push(onDayCreateGiven);
    }

    let options = {
        locale: _config.lang,
        formatDate: dateObj => dateObj.toLocaleDateString(_config.lang),
        ...defaultOptions,
        ...transformDataOptions(dataSet),
        onChange: function (selectedDates, dateStr, instance) {
            if (selectedDates.length !== 0){
                altField.value = dateToISOString(localDateToUTCDate(selectedDates[0]), options.appendAltTime);
                // set value of altFieldTo to defaultRange days after altField
                if (altFieldTo && options.mode === "range" && selectedDates.length === 1) {
                    const endDate = new Date(selectedDates[0]);
                    endDate.setDate(endDate.getDate() + options.defaultRange);
                    altFieldTo.value = dateToISOString(localDateToUTCDate(endDate), options.appendAltTime);

                } else if (altFieldTo && options.mode === "range" && selectedDates.length === 2) {
                    altFieldTo.value = dateToISOString(localDateToUTCDate(selectedDates[1]), options.appendAltTime);
                }
            }else{
                altField.value = '';
                // only clear altFieldTo in range mode (otherwise it probably doesn't exist)
                if (altFieldTo && options.mode === "range") {
                    altFieldTo.value = '';
                }
            }
        },
        onDayCreate: onDayCreateFunctions
    };

    // on mobile only 1 month always because of flatpickr bug
    if(matchMedia('(max-width: 767px)').matches){
        options = {...options, ...{
                showMonths: 1
            }}
    }

    return loadFlatPickr().then(function () {

        if(options.rangeSeparator !== null){
            Object.keys(flatpickr.l10ns).forEach(lang => {
                flatpickr.l10ns[lang].rangeSeparator = options.rangeSeparator;
            });
        }

        datepicker = input.flatpickr(options);

        if(options.mode === "range" && altField.value){
            let initialDateTo = altFieldTo.value ? new Date(altFieldTo.value) : new Date(altField.value);
            if (!altFieldTo.value){
                initialDateTo.setDate(initialDateTo.getDate() + options.defaultRange);
            }
            datepicker.setDate([new Date(altField.value), initialDateTo]);
        }else if(altField.value) {
            datepicker.setDate(new Date(altField.value));
        }

        function setMinDate(date) {
            datepicker.set('minDate', date);

            if (getDate(input) && getDate(input).getTime() < date.getTime()) {
                setDate(date);
            }
        }

        function setMaxDate(date) {
            datepicker.set('maxDate', date);

            if (getDate(input) && getDate(input).getTime() > date.getTime()) {
                setDate(date);
            }
        }

        function setEnable(dates) {
            datepicker.set('enable', dates);
        }

        function setDisable(dates) {
            datepicker.set('disable', dates);
        }

        function getDate() {
            return ISOStringToDate(findIn(selectors.altField, baseElement).value);
        }

        function setDate(date) {
            datepicker.setDate(new Date(date));
            datepicker.input.value = new Date(date).toLocaleDateString(_config.lang);
            altField.value = dateToISOString(localDateToUTCDate(date), options.appendAltTime);
            trigger('change', input);
        }

        function getInput() {
            return findIn(selectors.input, baseElement);
        }

        function getDatepickerInstance() {
            return datepicker;
        }

        let api = {
            getDate,
            setMinDate,
            setMaxDate,
            setEnable,
            setDisable,
            setDate,
            getInput,
            getDatepickerInstance
        };

        baseElement.datepicker = api;
        trigger(INITIALIZED_EVENT, baseElement);

        return api;
    });
}

function transformDataOptions(options) {
    options = {...options};

    if (options.minDate && typeof options.minDate === 'string') {
        options.minDate = UTCDateToLocalDate(ISOStringToDate(options.minDate));
    }

    if (options.maxDate && typeof options.maxDate === 'string') {
        options.maxDate = UTCDateToLocalDate(ISOStringToDate(options.maxDate));
    }

    if (options.appendAltTime && typeof options.appendAltTime === 'string') {
        options.appendAltTime = options.appendAltTime === 'true';
    }

    if(options.enable && typeof  options.enable === 'string') {
        options.enable = JSON.parse(options.enable);
    }

    if(options.disable && typeof  options.disable === 'string') {
        options.disable = JSON.parse(options.disable);
    }

    // make sure showMonths is a number (otherwise flatpickr has some weird behaviour - e.g. the week numbers are not shown)
    if (options.showMonths && typeof options.showMonths === 'string') {
        options.showMonths = options.showMonths * 1;
    }

    // convert defaultRange string to number (and subtract 1 to use 'logical' days)
    if (options.defaultRange && typeof options.defaultRange === 'string') {
        options.defaultRange = Number(options.defaultRange);
        if (options.defaultRange !== 0) {
            options.defaultRange -= 1;
        }
    }

    return options;
}

const INITIALIZED_EVENT = 'datepicker/initialized';

export function getApi(element) {
    if(element.datepicker) {
        return Promise.resolve(element.datepicker);
    } else {
        return new Promise(function(resolve, reject) {
            function initializeHandler() {
                resolve(element.datepicker);
                off(INITIALIZED_EVENT, initializeHandler, element);
            }
            on(INITIALIZED_EVENT, initializeHandler, element);
        });
    }
}

let promise;
export function loadFlatPickr() {
    if (promise) {
        return promise;
    }

    promise = new Promise(function(resolve, reject) {
        import("flatpickr").then(function () {
            if (_config.lang && _config.lang !== 'en') {
                import('flatpickr/dist/l10n/' + _config.lang + '.js').then(function () {
                    resolve();
                }).catch(function () {
                    resolve();
                });
            } else {
                resolve();
            }
        }).catch(function (error) {
            reject();
        });
    });

    return promise;
}